import { useEffect, useState } from "react";
import {
  AccountBaseline,
  ContentConcept,
  ContentEngineConfig,
  SocialLearningMemory,
  TrendReport,
  getContentEngineConfig,
  getRecentConcepts,
  getRecentTrendReports,
  getSocialLearningMemory,
  parseAccountBaselines,
} from "../../../../../database/community/contentEngine";

// One-shot loads, local to Home. Not in MediaStudioContext on purpose: most
// communities have no content engine yet, and these reads would run on every
// studio mount for everyone if they lived in the shared provider.

export type ContentEngineState = {
  loading: boolean;
  config: ContentEngineConfig | null;
  concepts: ContentConcept[];
  reports: TrendReport[];
  baselines: AccountBaseline[];
  learning: SocialLearningMemory | null;
};

export const useContentEngine = (
  communityId: string | undefined
): ContentEngineState => {
  const [state, setState] = useState<ContentEngineState>({
    loading: true,
    config: null,
    concepts: [],
    reports: [],
    baselines: [],
    learning: null,
  });

  useEffect(() => {
    if (!communityId) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    let cancelled = false;
    (async () => {
      const [config, concepts, reports, learning] = await Promise.all([
        getContentEngineConfig(communityId).catch(() => null),
        getRecentConcepts(communityId).catch(() => [] as ContentConcept[]),
        getRecentTrendReports(communityId).catch(() => [] as TrendReport[]),
        getSocialLearningMemory(communityId).catch(() => null),
      ]);
      if (cancelled) return;
      const withBaselines = reports.find((r) => r.accountBaselines);
      setState({
        loading: false,
        config,
        concepts,
        reports,
        learning,
        baselines: withBaselines
          ? parseAccountBaselines(withBaselines.accountBaselines)
          : [],
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [communityId]);

  return state;
};
